// ── CharacterVote ─────────────────────────────────────────────────────────────
// "Who's your favourite?" — vote for a character in a show, see what everyone else
// picked.
//
// This is a TV Time feature the alternatives board scores as "Character Vote", and
// the admin spelled out what counts (2026-07-29): a section with character AVATARS
// where users vote for a favourite fictional character. A name-only list explicitly
// does not qualify, which is why this leads with faces.
//
// Cast comes from TMDB's aggregate_credits (not /credits): aggregate rolls a role up
// across the whole series and gives episode_count, so the ordering is "who is
// actually in this show" rather than whoever billing happened to list first — a
// one-episode guest star otherwise lands beside the lead.
//
// NOT spoiler-gated. Some apps in that thread gate their cast behind watched
// episodes, but the main cast of a series is on the poster and in the trailer;
// hiding it would be theatre. Late-season additions ARE a genuine spoiler, which is
// what the episode_count ordering plus the TOP_N cap quietly handles — a character
// introduced in the final season doesn't have the screen time to reach the list.
import { useState, useEffect, useCallback } from 'react'
import { tmdbGet } from '../lib/tmdb'
import { supabase } from '../lib/supabase'
import { Trophy } from 'lucide-react'

const TOP_N = 12
const img = (path, size = 'w185') => path ? `https://image.tmdb.org/t/p/${size}${path}` : null

export function CharacterVote({ showId, user }) {
  const [cast, setCast]   = useState(null)
  const [tally, setTally] = useState({})   // person_id → votes
  const [mine, setMine]   = useState(null) // person_id
  const [busy, setBusy]   = useState(false)

  const loadTally = useCallback(async () => {
    const { data } = await supabase.rpc('character_vote_tally', { p_show: Number(showId) })
    setTally(Object.fromEntries((data || []).map(r => [r.person_id, r.votes])))
  }, [showId])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const c = await tmdbGet(`/tv/${showId}/aggregate_credits`)
        if (!alive) return
        const ranked = (c?.cast || [])
          .map(p => {
            const role = (p.roles || [])[0] || {}
            return {
              id: p.id,
              name: p.name,
              character: role.character || '',
              episodes: role.episode_count || 0,
              profile_path: p.profile_path,
            }
          })
          // A face is the point of the feature, and an unnamed role can't be voted for.
          .filter(p => p.profile_path && p.character)
          .sort((a, b) => b.episodes - a.episodes)
          .slice(0, TOP_N)
        setCast(ranked)
      } catch { if (alive) setCast([]) }
    })()
    return () => { alive = false }
  }, [showId])

  useEffect(() => { loadTally() }, [loadTally])

  useEffect(() => {
    if (!user?.id) return
    let alive = true
    supabase.from('character_votes').select('person_id')
      .eq('user_id', user.id).eq('show_id', Number(showId)).maybeSingle()
      .then(({ data }) => { if (alive) setMine(data?.person_id ?? null) })
    return () => { alive = false }
  }, [user?.id, showId])

  const vote = async (p) => {
    if (!user?.id || busy) return
    setBusy(true)
    const wasMine = mine === p.id
    // Optimistic: the tally is the reward for voting, so it has to move immediately.
    setMine(wasMine ? null : p.id)
    setTally(prev => {
      const n = { ...prev }
      if (mine) n[mine] = Math.max(0, (n[mine] || 0) - 1)
      if (!wasMine) n[p.id] = (n[p.id] || 0) + 1
      return n
    })
    let failed
    if (wasMine) {
      const { error } = await supabase.from('character_votes').delete().match({ user_id: user.id, show_id: Number(showId) })
      failed = error
    } else {
      // One row per (user, show) — changing your pick is an upsert onto that key,
      // never a second vote.
      const { error } = await supabase.from('character_votes').upsert({
        user_id: user.id, show_id: Number(showId), person_id: p.id,
        character_name: p.character, profile_path: p.profile_path,
      }, { onConflict: 'user_id,show_id' })
      failed = error
    }
    // loadTally re-reads the truth so the COUNTS self-correct, but `mine` stayed
    // optimistic — on a failure the card kept its amber "my pick" ring while the tally
    // beside it disagreed (audit 2026-07-29).
    if (failed) setMine(wasMine ? p.id : null)
    await loadTally()
    setBusy(false)
  }

  if (!cast) return null
  if (!cast.length) return null

  const total = Object.values(tally).reduce((a, b) => a + b, 0)
  const leader = Object.entries(tally).sort((a, b) => b[1] - a[1])[0]

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: 4 }}>
        <Trophy size={14} color="var(--amber)" style={{ alignSelf: 'center' }} />
        <span style={{ color: 'var(--text-strong)', fontWeight: 800, fontSize: 15 }}>Favourite character</span>
        {total > 0 && (
          <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>
            {total} {total === 1 ? 'vote' : 'votes'}
          </span>
        )}
      </div>
      <div style={{ color: 'var(--text-dim)', fontSize: 12, marginBottom: 12 }}>
        {mine ? 'Tap another to change your pick.' : 'Pick one. You can change it later.'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 10 }}>
        {cast.map(p => {
          const votes = tally[p.id] || 0
          const isMine = mine === p.id
          const isLeader = leader && Number(leader[0]) === p.id && leader[1] > 0
          const pct = total ? Math.round((votes / total) * 100) : 0
          return (
            <button key={p.id} onClick={() => vote(p)} disabled={!user || busy} style={{
              position: 'relative', padding: 0, borderRadius: 'var(--radius-md)', overflow: 'hidden',
              background: 'var(--bg-card)', cursor: user ? 'pointer' : 'default', fontFamily: 'var(--font)',
              border: `1.5px solid ${isMine ? 'var(--amber)' : 'var(--border)'}`,
              boxShadow: isMine ? '0 0 0 3px rgba(var(--amber-rgb),0.16)' : 'none',
              textAlign: 'left',
            }}>
              <div style={{ position: 'relative', aspectRatio: '2/3', background: 'var(--surface)' }}>
                <img src={img(p.profile_path)} alt="" loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                {isLeader && (
                  <div style={{ position: 'absolute', top: 5, left: 5, background: 'var(--amber)', color: 'var(--amber-ink)',
                    borderRadius: 'var(--radius-pill)', padding: '2px 7px', fontSize: 9.5, fontWeight: 800 }}>
                    ★ Top
                  </div>
                )}
                {/* Share of the vote as a fill under the face — a bar chart would need
                    its own row per character and this reads at a glance. */}
                {votes > 0 && (
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3,
                    background: 'rgba(0,0,0,0.5)' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: isMine ? 'var(--amber)' : 'var(--purple)' }}/>
                  </div>
                )}
              </div>
              <div style={{ padding: '7px 8px 8px' }}>
                {/* Two lines, not one-line-with-ellipsis: at this width almost every
                    real character name truncated ("Jesse Pink…", "Hank Schra…"), and a
                    vote card whose label you can't read is a poor ask. minHeight keeps
                    the grid even when one name wraps and its neighbour doesn't. */}
                <div style={{ color: isMine ? 'var(--amber)' : 'var(--text-strong)', fontWeight: 700, fontSize: 11.5,
                  lineHeight: 1.25, minHeight: '2.5em',
                  overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{p.character}</div>
                <div style={{ color: 'var(--text-dim)', fontSize: 10, marginTop: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {votes > 0 ? `${votes} ${votes === 1 ? 'vote' : 'votes'}` : p.name}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
